import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import '@/App.css';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Home from '@/pages/HomeFlagship';
import NotFound from '@/pages/NotFound';
import { captureAttribution } from '@/lib/attribution';
import { initTracking } from '@/lib/analytics';

/*
 * Route-level code splitting. The homepage stays in the main bundle so its first paint
 * never waits on a second request; every other page, with its content data, loads with
 * its route. Before this, all ~60 pages (and every article's text) shipped in one
 * 1.8 MB bundle that each visitor downloaded and parsed whatever page they opened.
 *
 * After a deploy, a tab opened on the previous build can ask for a chunk that no longer
 * exists. Reload once to pick up the new build rather than leave a blank page.
 */
const RELOAD_KEY = 'hm-chunk-reload';
function lazyPage(load, pick = 'default') {
  return lazy(() =>
    load()
      .then((m) => {
        try { sessionStorage.removeItem(RELOAD_KEY); } catch (e) { /* storage unavailable */ }
        return { default: m[pick] };
      })
      .catch((err) => {
        try {
          if (!sessionStorage.getItem(RELOAD_KEY)) {
            sessionStorage.setItem(RELOAD_KEY, '1');
            window.location.reload();
            return new Promise(() => {});
          }
        } catch (e) { /* storage unavailable: surface the original error */ }
        throw err;
      })
  );
}

const Homeowners = lazyPage(() => import('@/pages/Homeowners'));
const QuotePage = lazyPage(() => import('@/pages/QuotePage'));
const Contractors = lazyPage(() => import('@/pages/Contractors'));
const ContractorLeads = lazyPage(() => import('@/pages/ContractorLeads'));
const GrowthAssessment = lazyPage(() => import('@/pages/GrowthAssessment'));
const Growth = lazyPage(() => import('@/pages/Growth'));
const Services = lazyPage(() => import('@/pages/Services'));
const ServicePage = lazyPage(() => import('@/pages/ServicePage'));
const PhpMigration = lazyPage(() => import('@/pages/PhpMigration'));
const HowItWorks = lazyPage(() => import('@/pages/HowItWorks'));
const CaseStudies = lazyPage(() => import('@/pages/CaseStudies'));
const About = lazyPage(() => import('@/pages/About'));
const Contact = lazyPage(() => import('@/pages/Contact'));
const SiteMap = lazyPage(() => import('@/pages/SiteMap'));
const Legal = lazyPage(() => import('@/pages/Legal'));
const ThankYou = lazyPage(() => import('@/pages/ThankYou'));
const CostEstimator = lazyPage(() => import('@/pages/CostEstimator'));
const LocationPage = lazyPage(() => import('@/pages/LocationPage'));
const LocationsIndex = lazyPage(() => import('@/pages/LocationPage'), 'LocationsIndex');
const RenovationsHub = lazyPage(() => import('@/pages/RenovationsHub'));
const IndustriesHub = lazyPage(() => import('@/pages/IndustriesHub'));
const IndustryPage = lazyPage(() => import('@/pages/IndustryPage'));
const ConstructionPage = lazyPage(() => import('@/pages/ConstructionPage'));
const HomeRenovationPage = lazyPage(() => import('@/pages/HomeRenovationPage'));
const RoofingPage = lazyPage(() => import('@/pages/RoofingPage'));
const ElectricalPage = lazyPage(() => import('@/pages/ElectricalPage'));
const PaintingPage = lazyPage(() => import('@/pages/PaintingPage'));
const HvacPage = lazyPage(() => import('@/pages/HvacPage'));
const PlumbingPage = lazyPage(() => import('@/pages/PlumbingPage'));
const PestControlPage = lazyPage(() => import('@/pages/PestControlPage'));
const CleaningPage = lazyPage(() => import('@/pages/CleaningPage'));
const LandscapingPage = lazyPage(() => import('@/pages/LandscapingPage'));
const RenovationPage = lazyPage(() => import('@/pages/RenovationPage'));
const ResourcesHub = lazyPage(() => import('@/pages/ResourcesHub'));
const ResourceArticle = lazyPage(() => import('@/pages/ResourceArticle'));
const ResourcesParam = lazyPage(() => import('@/pages/ResourcesParam'));
const ToolsHub = lazyPage(() => import('@/pages/ToolsHub'));
const RenovationPlanner = lazyPage(() => import('@/pages/tools/RenovationPlanner'));
const QuoteComparison = lazyPage(() => import('@/pages/tools/QuoteComparison'));
const BudgetPlanner = lazyPage(() => import('@/pages/tools/BudgetPlanner'));
const ContractorChecklist = lazyPage(() => import('@/pages/tools/ContractorChecklist'));
const RenovationChecklist = lazyPage(() => import('@/pages/tools/RenovationChecklist'));
const RenovationLeadsHub = lazyPage(() => import('@/pages/ContractorLeadPages'), 'RenovationLeadsHub');
const AppointmentsPage = lazyPage(() => import('@/pages/ContractorLeadPages'), 'AppointmentsPage');
const ContractorSlug = lazyPage(() => import('@/pages/ContractorSlug'));

// Most pages open on a navy hero, so a navy, full-height placeholder keeps the footer
// below the fold and avoids a light flash while a route's chunk arrives.
function RouteFallback() {
  return <div className="min-h-screen bg-navy" aria-busy="true" />;
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    // The target may sit in a route chunk that is still loading, so look for it for a
    // couple of seconds before giving up and starting at the top.
    let tries = 0;
    let timer;
    const find = () => {
      let el = null;
      try { el = document.querySelector(hash); } catch (e) { /* not a valid selector */ }
      if (el) {
        timer = setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
      } else if (tries++ < 25) {
        timer = setTimeout(find, 100);
      } else {
        window.scrollTo(0, 0);
      }
    };
    find();
    return () => clearTimeout(timer);
  }, [pathname, hash]);
  return null;
}

function TrailingSlashRedirect() {
  const { pathname, search } = useLocation();
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return <Navigate to={pathname.slice(0, -1) + search} replace />;
  }
  return null;
}

function AppShell() {
  useEffect(() => {
    captureAttribution();
    initTracking();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const lenis = new Lenis({ lerp: 0.11, wheelMultiplier: 1 });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <>
      <ScrollManager />
      <TrailingSlashRedirect />
      <Header />
      <main id="main-content">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/homeowners" element={<Homeowners />} />
            <Route path="/get-renovation-quotes" element={<QuotePage />} />
            <Route path="/renovation-cost-estimator" element={<CostEstimator />} />
            <Route path="/locations" element={<LocationsIndex />} />
            <Route path="/locations/:province" element={<LocationPage />} />
            <Route path="/locations/:province/:city" element={<LocationPage />} />
            <Route path="/industries" element={<IndustriesHub />} />
            {/* Bespoke composition; must precede the generic :slug route. */}
            <Route path="/industries/construction" element={<ConstructionPage />} />
            <Route path="/industries/home-renovation" element={<HomeRenovationPage />} />
            <Route path="/industries/roofing" element={<RoofingPage />} />
            <Route path="/industries/electrical" element={<ElectricalPage />} />
            <Route path="/industries/painting" element={<PaintingPage />} />
            <Route path="/industries/hvac" element={<HvacPage />} />
            <Route path="/industries/plumbing" element={<PlumbingPage />} />
            <Route path="/industries/pest-control" element={<PestControlPage />} />
            <Route path="/industries/cleaning" element={<CleaningPage />} />
            <Route path="/industries/landscaping" element={<LandscapingPage />} />
            <Route path="/industries/:slug" element={<IndustryPage />} />
            <Route path="/renovations" element={<RenovationsHub />} />
            <Route path="/renovations/:slug" element={<RenovationPage />} />
            <Route path="/resources" element={<ResourcesHub />} />
            <Route path="/resources/:audience/:slug" element={<ResourceArticle />} />
            <Route path="/resources/:param" element={<ResourcesParam />} />
            <Route path="/tools" element={<ToolsHub />} />
            <Route path="/tools/renovation-planner" element={<RenovationPlanner />} />
            <Route path="/tools/compare-renovation-quotes" element={<QuoteComparison />} />
            <Route path="/tools/renovation-budget-planner" element={<BudgetPlanner />} />
            <Route path="/tools/contractor-checklist" element={<ContractorChecklist />} />
            <Route path="/tools/renovation-checklist" element={<RenovationChecklist />} />
            <Route path="/contractors" element={<Contractors />} />
            <Route path="/contractors/get-qualified-leads" element={<ContractorLeads />} />
            <Route path="/contractors/grow-your-business" element={<Growth />} />
            <Route path="/contractors/renovation-leads" element={<RenovationLeadsHub />} />
            <Route path="/contractors/renovation-appointments" element={<AppointmentsPage />} />
            <Route path="/contractors/:slug" element={<ContractorSlug />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServicePage />} />
            <Route path="/php-migration" element={<PhpMigration />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/about" element={<About />} />
            <Route path="/growth-assessment" element={<GrowthAssessment />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/sitemap" element={<SiteMap />} />
            <Route path="/privacy-policy" element={<Legal page="privacy-policy" />} />
            <Route path="/terms" element={<Legal page="terms" />} />
            <Route path="/accessibility" element={<Legal page="accessibility" />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}
